import { useCallback, useContext, useMemo, useState, type Dispatch, type PropsWithChildren, type SetStateAction } from 'react'
import { useToast } from '../../../context/useToast'
import { WorkspaceContext } from '../../../context/workspaceContextCore'
import type { WorkspaceAction } from '../../../utils/workspacePermissions'
import { AiMagicIcon, BackIcon, HomeIcon, MoreIcon, NewIcon, SearchIcon } from './icons'
import { Orbit } from './Orbit'
import { OrbitRegistryContext, type OrbitRegistryValue } from './orbitRegistryContext'
import { OrbitContext, type OrbitContextValue } from './orbitContext'
import type { OrbitOpenState, OrbitPageDescriptor, OrbitSlice, OrbitTarget } from './orbitTypes'

export type { OrbitContextValue }

function buildPagedSlices(
  allContextSlices: OrbitSlice[],
  pageIndex: number,
  globalsList: OrbitSlice[],
  setOpenState: Dispatch<SetStateAction<OrbitOpenState | null>>,
): OrbitSlice[] {
  if (allContextSlices.length <= 4) {
    return [...globalsList, ...allContextSlices]
  }

  const pageSize = 3
  const totalPages = Math.ceil(allContextSlices.length / pageSize)
  const current = pageIndex % totalPages
  const startIndex = current * pageSize
  const pageSlices = allContextSlices.slice(startIndex, startIndex + pageSize)
  const hasNext = current < totalPages - 1

  if (hasNext) {
    const moreSlice: OrbitSlice = {
      id: 'ctx-more',
      label: 'More…',
      icon: MoreIcon,
      onFire: () => {
        setOpenState((prev) => {
          if (!prev) return null
          return {
            ...prev,
            slices: buildPagedSlices(allContextSlices, current + 1, globalsList, setOpenState),
          }
        })
      },
    }
    return [...globalsList, ...pageSlices, moreSlice]
  } else {
    const backSlice: OrbitSlice = {
      id: 'ctx-back',
      label: 'Back…',
      icon: BackIcon,
      onFire: () => {
        setOpenState((prev) => {
          if (!prev) return null
          return {
            ...prev,
            slices: buildPagedSlices(allContextSlices, 0, globalsList, setOpenState),
          }
        })
      },
    }
    return [...globalsList, ...pageSlices, backSlice]
  }
}

export const OrbitProvider = ({ children }: PropsWithChildren) => {
  const [descriptors, setDescriptors] = useState<OrbitPageDescriptor[]>([])
  const activeDescriptor = descriptors[descriptors.length - 1] ?? null
  const [openState, setOpenState] = useState<OrbitOpenState | null>(null)
  const { showToast } = useToast()
  const workspaceContext = useContext(WorkspaceContext)

  const activeWorkspaceId = workspaceContext?.activeWorkspaceId
  const workspaces = workspaceContext?.workspaces
  const canPerform = workspaceContext?.canPerform
  const activeWorkspace = workspaces?.find((w) => w.id === activeWorkspaceId)
  const workspaceSlug = activeWorkspace?.slug || activeWorkspace?.id || activeWorkspaceId

  const globals = useMemo<OrbitSlice[]>(() => {
    const canUseAi = Boolean(workspaceSlug && (!canPerform || canPerform('ai.use' as WorkspaceAction)))
    const hasCreateAction = Boolean(
      activeDescriptor?.onCreate ||
      activeDescriptor?.targets?.canvas ||
      (activeDescriptor && activeDescriptor.page === 'dashboard')
    )

    return [
      {
        id: 'new',
        label: 'New',
        icon: NewIcon,
        disabled: !hasCreateAction,
        disabledReason: !hasCreateAction ? 'New item creation is not available for this page' : undefined,
        onFire: async () => {
          if (activeDescriptor?.onCreate) {
            await activeDescriptor.onCreate()
            return
          }
          const canvasTarget = activeDescriptor?.targets?.canvas
          if (canvasTarget) {
            const dummy = typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement)
            const canvasSlices = canvasTarget(dummy)
            const createSlice = canvasSlices.find((s) => s.id.includes('new') || s.id.includes('create'))
            if (createSlice) {
              await createSlice.onFire()
              return
            }
          }
          showToast('New item creation is not available for this page')
        },
      },
      {
        id: 'search',
        label: 'Search',
        icon: SearchIcon,
        disabled: true,
        disabledReason: 'Search is not available from this context',
        onFire: () => {
          showToast('Search is not available from this context')
        },
      },
      {
        id: 'ai',
        label: 'AI Magic',
        icon: AiMagicIcon,
        disabled: !canUseAi,
        disabledReason: !canUseAi ? 'AI Magic is only available inside an active workspace' : undefined,
        onFire: () => {
          if (!canUseAi) {
            showToast('AI Magic is only available inside an active workspace')
            return
          }
          const aiPath = `/console/workspace/${workspaceSlug}/d/ai`
          window.dispatchEvent(new CustomEvent('timelining:checklist-request-close'))
          window.history.pushState(null, '', aiPath)
          window.dispatchEvent(new PopStateEvent('popstate'))
        },
      },
      {
        id: 'home',
        label: 'Home',
        icon: HomeIcon,
        onFire: () => {
          const homePath = workspaceSlug ? `/console/workspace/${workspaceSlug}/d` : '/console'
          window.dispatchEvent(new CustomEvent('timelining:checklist-request-close'))
          window.history.pushState(null, '', homePath)
          window.dispatchEvent(new PopStateEvent('popstate'))
          showToast('Home')
        },
      },
    ]
  }, [activeDescriptor, canPerform, showToast, workspaceSlug])

  const close = useCallback(() => setOpenState(null), [])

  const registerDescriptor = useCallback((desc: OrbitPageDescriptor) => {
    setDescriptors((prev) => {
      if (prev.includes(desc)) return prev
      return [...prev, desc]
    })
    return () => {
      setDescriptors((prev) => {
        if (!prev.includes(desc)) return prev
        return prev.filter((d) => d !== desc)
      })
    }
  }, [])

  const setDescriptor = useCallback((desc: OrbitPageDescriptor | null) => {
    setDescriptors((prev) => {
      const current = prev[prev.length - 1] ?? null
      if (current === desc && prev.length === (desc ? 1 : 0)) return prev
      return desc ? [desc] : []
    })
  }, [])

  const registry = useMemo<OrbitRegistryValue>(
    () => ({ registerDescriptor, setDescriptor }),
    [registerDescriptor, setDescriptor],
  )

  const open = useCallback(
    (at: { x: number; y: number }, target?: OrbitTarget, targetNode?: HTMLElement) => {
      if (!activeDescriptor) return

      setOpenState((prev) => {
        if (prev) return null

        const contextSlices = target && targetNode
          ? activeDescriptor.targets?.[target]?.(targetNode) ?? activeDescriptor.contextSlices()
          : activeDescriptor.contextSlices()

        return {
          x: at.x,
          y: at.y,
          slices: buildPagedSlices(contextSlices, 0, globals, setOpenState),
          globalCount: globals.length,
          centerLabel: activeDescriptor.centerLabel,
          page: activeDescriptor.page,
        }
      })
    },
    [activeDescriptor, globals],
  )

  const handleSliceFire = useCallback(
    async (slice: OrbitSlice) => {
      if (slice.id === 'ctx-more' || slice.id === 'ctx-back') {
        try {
          await Promise.resolve(slice.onFire())
        } catch (err) {
          console.error('[orbit] slice paging failed', slice.id, err)
        }
        return
      }

      try {
        await Promise.resolve(slice.onFire())
      } catch (err) {
        console.error('[orbit] slice action failed', slice.id, err)
        showToast(err instanceof Error ? err.message : 'Action failed')
      } finally {
        close()
      }
    },
    [close, showToast],
  )

  const value = useMemo<OrbitContextValue>(
    () => ({
      descriptor: activeDescriptor,
      openState,
      setDescriptor,
      registerDescriptor,
      open,
      close,
    }),
    [activeDescriptor, close, open, openState, registerDescriptor, setDescriptor],
  )

  return (
    <OrbitRegistryContext.Provider value={registry}>
      <OrbitContext.Provider value={value}>
        {children}
        {openState ? (
          <Orbit state={openState} onClose={close} onSliceFire={handleSliceFire} />
        ) : null}
      </OrbitContext.Provider>
    </OrbitRegistryContext.Provider>
  )
}
